import axiosInstance from './axiosInstance';

export const linksApi = {
  getAll: async () => {
    try {
      const response = await axiosInstance.get('/links');
      console.log('Links API response:', {
        status: response.status,
        data: response.data,
        isArray: Array.isArray(response.data)
      });
      return response;
    } catch (error) {
      console.error('Links API error:', error);
      throw error;
    }
  },
  create: (link) => axiosInstance.post('/links', link),
  update: (id, link) => axiosInstance.put(`/links/${id}`, link),
  delete: (id) => axiosInstance.delete(`/links/${id}`),
  
  /**
   * Download QR code for a link
   * @param {string} id - Unique ID of the link
   * @param {string} linkName - Optional custom filename for the download
   * @returns {Promise<void>} - Initiates download in browser
   */
  downloadQRCode: async (id, linkName) => {
    if (!id) throw new Error('Link ID is required');
    
    try {
      // Convert ObjectId to string and validate format
      // Handle both string IDs and ObjectId objects
      let linkIdString;
      if (typeof id === 'object' && id !== null) {
        // If it's a MongoDB ObjectId object, convert to string
        linkIdString = id.toString();
      } else {
        // If it's already a string, use it directly
        linkIdString = String(id);
      }
      
      // Trim the ID to remove any leading/trailing spaces
      linkIdString = linkIdString.trim();
      
      console.log(`Downloading QR code for link with ID: ${linkIdString}...`);
      console.log('Link ID details:', {
        originalId: id,
        originalIdType: typeof id,
        stringId: linkIdString,
        idLength: linkIdString.length,
        isObjectId: typeof id === 'object' && id !== null && typeof id.toString === 'function'
      });
      
      // Basic validation for MongoDB ObjectId format (24 hex characters)
      if (!/^[0-9a-fA-F]{24}$/.test(linkIdString)) {
        throw new Error(`Invalid link ID format. Expected 24 hex characters, got: ${linkIdString} (${linkIdString.length} chars)`);
      }
      
      // Prepare filename
      const sanitizedName = linkName ? 
        linkName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') : 
        'qrcode';
      
      const filename = `${sanitizedName}-qr-code.png`;
      
      console.log('Sending QR code download request...');
      const response = await axiosInstance.get(`/links/${encodeURIComponent(linkIdString)}/qr-code`, {
        responseType: 'blob', // Important: get response as blob for file download
        timeout: 30000, // 30 seconds timeout
        headers: {
          'Accept': 'image/png,*/*'
        }
      });
      
      // Validate response
      if (!response.data || response.data.size === 0) {
        throw new Error('Received empty response from server');
      }
      
      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('image/png')) {
        console.warn('Unexpected content type:', contentType);
      }
      
      console.log('QR code downloaded successfully:', {
        size: response.data.size,
        type: response.data.type,
        contentType: contentType
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link and click it
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Append to body and click
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('QR code download initiated successfully');
      return;
    } catch (error) {
      console.error(`Error downloading QR code for link with ID ${id}:`, error);
      
      // Extract and log response details if available
      if (error.response) {
        const status = error.response.status;
        const headers = error.response.headers;
        let errorData = error.response.data;
        
        // Try to parse error data if it's a blob
        if (errorData instanceof Blob) {
          try {
            const reader = new FileReader();
            const textPromise = new Promise((resolve, reject) => {
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
            });
            
            reader.readAsText(errorData);
            const errorText = await textPromise;
            
            try {
              errorData = JSON.parse(errorText);
            } catch (e) {
              errorData = { rawError: errorText };
            }
            
            console.error('Error response data:', errorData);
          } catch (e) {
            console.error('Could not parse error blob:', e);
          }
        }
        
        // Provide user-friendly error messages based on status
        if (status === 404) {
          throw new Error('Link not found or QR code could not be generated.');
        } else if (status === 400) {
          throw new Error(`Invalid request: ${errorData?.details || 'Check link ID format'}`);
        } else if (status === 401) {
          throw new Error('You are not authenticated. Please log in and try again.');
        } else if (status === 403) {
          throw new Error('You do not have permission to download this QR code.');
        } else if (status === 500) {
          throw new Error(`Server error: ${errorData?.details || 'An internal error occurred while generating the QR code. Please try again later.'}`);
        } else {
          throw new Error(`QR code download failed (${status}): ${errorData?.error || errorData?.details || 'Unknown server error'}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server. Please check your network connection and try again.');
      } else {
        throw new Error(`QR code download error: ${error.message}`);
      }
    }
  },

  /**
   * Get QR code data URL for display purposes
   * @param {string} id - Unique ID of the link
   * @returns {Promise<Object>} - QR code data with dataUrl and url
   */
  getQRCodeDataURL: async (id) => {
    try {
      if (!id) throw new Error('Link ID is required');

      // Convert ObjectId to string and validate format
      // Handle both string IDs and ObjectId objects
      let linkIdString;
      if (typeof id === 'object' && id !== null) {
        // If it's a MongoDB ObjectId object, convert to string
        linkIdString = id.toString();
      } else {
        // If it's already a string, use it directly
        linkIdString = String(id);
      }
      
      console.log(`Getting QR code data URL for link with ID: ${linkIdString}...`);
      console.log('Link ID details for data URL:', {
        originalId: id,
        originalIdType: typeof id,
        stringId: linkIdString,
        idLength: linkIdString.length,
        isObjectId: typeof id === 'object' && id !== null && typeof id.toString === 'function'
      });
      
      // Basic validation for MongoDB ObjectId format (24 hex characters)
      if (!/^[0-9a-fA-F]{24}$/.test(linkIdString)) {
        throw new Error(`Invalid link ID format. Expected 24 hex characters, got: ${linkIdString} (${linkIdString.length} chars)`);
      }
      
      const response = await axiosInstance.get(`/links/${encodeURIComponent(linkIdString)}/qr-data-url`);
      
      if (!response.data || !response.data.dataUrl) {
        throw new Error('Server returned invalid QR code data URL response');
      }
      
      console.log('QR code data URL obtained successfully');
      return response.data; // Return the full response object
      
    } catch (error) {
      console.error(`Error getting QR code data URL for link with ID ${id}:`, error);
      
      if (error.response) {
        const { status, data: errorData } = error.response;
        if (status === 404) {
          throw new Error('Link not found or QR code could not be generated.');
        } else if (status >= 500) {
          throw new Error('Server error generating QR code data URL. Please try again.');
        } else {
          throw new Error(`QR code data URL failed (${status}): ${errorData?.error || 'Unknown server error'}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server. Check your network connection.');
      } else {
        throw new Error(`QR code data URL error: ${error.message}`);
      }
    }
  },

  /**
   * Generate custom QR code with design options
   * @param {string} id - Unique ID of the link
   * @param {Object} customOptions - Custom design options for QR code
   * @returns {Promise<Object>} - QR code data with custom design
   */
  getCustomQRCode: async (id, customOptions = {}) => {
    try {
      if (!id) throw new Error('Link ID is required');

      // Convert ObjectId to string and validate format
      // Handle both string IDs and ObjectId objects
      let linkIdString;
      if (typeof id === 'object' && id !== null) {
        // If it's a MongoDB ObjectId object, convert to string
        linkIdString = id.toString();
      } else {
        // If it's already a string, use it directly
        linkIdString = String(id);
      }
      
      console.log(`Generating custom QR code for link with ID: ${linkIdString}...`, customOptions);
      console.log('Link ID details for custom QR:', {
        originalId: id,
        originalIdType: typeof id,
        stringId: linkIdString,
        idLength: linkIdString.length,
        isObjectId: typeof id === 'object' && id !== null && typeof id.toString === 'function'
      });
      
      // Basic validation for MongoDB ObjectId format (24 hex characters)
      if (!/^[0-9a-fA-F]{24}$/.test(linkIdString)) {
        throw new Error(`Invalid link ID format. Expected 24 hex characters, got: ${linkIdString} (${linkIdString.length} chars)`);
      }
      
      const response = await axiosInstance.post(`/links/${encodeURIComponent(linkIdString)}/qr-custom`, customOptions);
      
      if (!response.data || !response.data.dataUrl) {
        throw new Error('Server returned invalid custom QR code response');
      }
      
      console.log('Custom QR code generated successfully');
      return response.data; // Return the full response object
      
    } catch (error) {
      console.error(`Error generating custom QR code for link with ID ${id}:`, error);
      
      if (error.response) {
        const { status, data: errorData } = error.response;
        if (status === 404) {
          throw new Error('Link not found or QR code could not be generated.');
        } else if (status === 400) {
          throw new Error(`Invalid custom options: ${errorData?.error || 'Bad request'}`);
        } else if (status >= 500) {
          throw new Error('Server error generating custom QR code. Please try again.');
        } else {
          throw new Error(`Custom QR code generation failed (${status}): ${errorData?.error || 'Unknown server error'}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server. Check your network connection.');
      } else {
        throw new Error(`Custom QR code generation error: ${error.message}`);
      }
    }
  },
  
  /**
   * Download custom QR code directly from data URL
   * @param {string} dataUrl - The data URL of the custom QR code
   * @param {string} linkName - The name of the link (for filename)
   * @param {string} format - The format of the QR code (png, jpeg, etc.)
   */
  downloadCustomQRCode: (dataUrl, linkName, format = 'png') => {
    if (!dataUrl) {
      throw new Error('No QR code data URL provided');
    }
    
    try {
      console.log('Downloading custom QR code from data URL...');
      
      // Sanitize the name for use in filename
      const sanitizedName = linkName ? 
        linkName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') : 
        'qr-code';
      
      // Create a download link
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${sanitizedName}-custom-qr.${format}`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      console.log('Custom QR code download initiated');
      return true;
    } catch (error) {
      console.error('Error downloading custom QR code:', error);
      throw new Error(`Failed to download custom QR code: ${error.message}`);
    }
  }
};

export default linksApi;